import { useEffect, useMemo, useState, useContext } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import InterventionPointIssues from './InterventionPointIssues';
import InterventionPointDeployments from './InterventionPointDeployments';
import InterventionPointsDetail from './InterventionPointsDetail';
import AppContext from '../contexts/AppContext';

const MAX_RESULTS = 25;

const matchesCriteria = (attr, crit) => {
  const name = attr?.name || '';
  const id = String(attr?.id || '');
  return (crit?.name && name === crit.name) || (crit?.id && id === String(crit.id));
};

export default function InterventionPoints() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [points, setPoints] = useState([]); // [{ id, key, label }]
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // { id, key, label }
  const [detail, setDetail] = useState(null); // payload from /intervention-points/:key
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailCache, setDetailCache] = useState({}); // key -> detail
  const [detailLoadingKeys, setDetailLoadingKeys] = useState({}); // key -> boolean
  const { serviceDesk } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;

  const ATTR_CONFIG = serviceDeskConfig.interventionPointConfig;

  useEffect(() => {
    let cancelled = false;
    async function loadList() {
      try {
        setLoading(true);
        setError('');
        const res = await fetchWithAuth('intervention-points');
        const body = await res.json();
        const values = body?.data?.values || body?.values || [];
        const mapped = Array.isArray(values)
          ? values.map((v) => ({
              id: String(v.id || v.objectId || v.key || ''),
              key: String(v.key || v.objectKey || v.id || ''),
              label: String(v.label || v.name || v.displayName || v.key || ''),
              sap_code: String(v.sap_code || ''),
            }))
          : [];
        if (!cancelled) setPoints(mapped);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Erreur inconnue');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadList();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = String(query || '').toLowerCase().trim();

    if (!q) return points;
    return points.filter((p) =>
      p.label.toLowerCase().includes(q) ||
      p.sap_code?.toLowerCase().includes(q)
    );
  }, [points, query]);

  const ensureDetail = async (pointObjectId) => {
    if (!pointObjectId) return null;
    if (detailCache[pointObjectId]) return detailCache[pointObjectId];
    if (detailLoadingKeys[pointObjectId]) return null;
    try {
      setDetailLoadingKeys(prev => ({ ...prev, [pointObjectId]: true }));
      const res = await fetchWithAuth(`intervention-points/${encodeURIComponent(pointObjectId)}/attributes`);
      const body = await res.json();
      const d = body?.data || body || null;
      setDetailCache(prev => ({ ...prev, [pointObjectId]: d }));
      return d;
    } catch (e) {
      setError(e?.message || 'Erreur inconnue');
      return null;
    } finally {
      setDetailLoadingKeys(prev => ({ ...prev, [pointObjectId]: false }));
    }
  };

  const onSelectPoint = async (opt) => {
    if (!opt) {
      setSelected(null);
      setDetail(null);
      return;
    }
    setSelected(opt);
    setDetail(null);
    try {
      setDetailLoading(true);
      const d = await ensureDetail(opt.id);
      setDetail(d);
    } catch (e) {
      setError(e?.message || 'Erreur inconnue');
    } finally {
      setDetailLoading(false);
    }
  };

  const getAttrValues = (detailObj, matcher) => {
    try {
      const items = Array.isArray(detailObj)
        ? detailObj
        : (Array.isArray(detailObj?.attributes) ? detailObj.attributes : []);

      for (const item of items) {
        const name = item?.objectTypeAttribute?.name || item?.name || '';
        const id = String(item?.objectTypeAttributeId || item?.id || '');
        const valuesArray = (item?.objectAttributeValues || item?.values || []);
        const mappedValues = valuesArray
          .map(v => {
            if (v?.referencedType && v?.referencedObject) {
              return v.referencedObject.label || v.displayValue || v.value;
            }
            return v?.displayValue || v?.value;
          })
          .filter(Boolean);

        if (typeof matcher === 'string') {
          if (name === matcher || id === matcher) return mappedValues;
        } else if (typeof matcher === 'function') {
          if (matcher({ name, id, item })) return mappedValues;
        }
      }
      return [];
    } catch (_) {
      return [];
    }
  };

  const getAllAttributes = (detailObj) => {
    try {
      const items = Array.isArray(detailObj)
        ? detailObj
        : (Array.isArray(detailObj?.attributes) ? detailObj.attributes : []);

      return items.map(item => {
        const id = String(item?.objectTypeAttributeId || item?.id || '');
        const name = item?.objectTypeAttribute?.name || item?.name || id;
        const valuesArray = (item?.objectAttributeValues || item?.values || []);
        const values = valuesArray
          .map(v => {
            if (v?.referencedType && v?.referencedObject) {
              return v.referencedObject.label || v.displayValue || v.value;
            }
            return v?.displayValue || v?.value;
          })
          .filter(Boolean);
        return { id, name, values };
      });
    } catch (_) {
      return [];
    }
  };

  // Helper using local getAttrValues; supports criteria with name or id
  const valuesFor = (detailObj, criteria) => {
    const matchFn = ({ name, id }) => matchesCriteria({ name, id }, criteria);
    return getAttrValues(detailObj, matchFn);
  };

  // Prefetch details for the first results to show sector and furniture count on cards
  useEffect(() => {
    if (filtered.length > MAX_RESULTS) return;

    const prefetch = async () => {
      const first = filtered.slice(0, MAX_RESULTS);
      for (const p of first) {
        if (!detailCache[p.id] && !detailLoadingKeys[p.id]) {
          ensureDetail(p.id);
        }
      }
    };
    prefetch();
  }, [filtered, detailCache, detailLoadingKeys]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="container mx-auto px-4 py-6">
          {/* <h1 className="mb-4 text-2xl font-bold text-black">Points d'intervention</h1> */}

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-black"></div>
            </div>
          ) : error ? (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          ) : (
            <div className="rounded border border-gray-200 bg-white p-4">
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Rechercher un magasin</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Tapez le nom ou code SAP du magasin…"
                      className="mt-1 block w-full rounded-md border p-2 pr-10 text-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    />
                    {query.length > 0 && (
                      <button
                        type="button"
                        onClick={() => { setQuery(''); setSelected(null); setDetail(null); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-200 text-gray-600 hover:bg-gray-300"
                        aria-label="Effacer la recherche"
                        title="Effacer"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
              </div>

              <div className="mb-2">
                {filtered.length > 0 && !selected && (
                  <p className="text-xs text-gray-500">{filtered.length} magasins</p>
                )}
              </div>

              {query.length > 0 && !selected && filtered.length > MAX_RESULTS && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  Trop de résultats ({filtered.length}). Veuillez affiner votre recherche.
                </div>
              )}

              {query.length === 0 && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
                Veuillez entrer un nom ou code SAP de magasin pour afficher les résultats.
                </div>
              )}

              {query.length > 0 && !selected && filtered.length === 0 && (
                <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900">
                  Aucun magasin ne correspond à votre recherche.
                </div>
              )}

              {!selected && filtered.length > 0 && filtered.length <= MAX_RESULTS && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {filtered.slice(0, MAX_RESULTS).map((p) => {
                    const d = detailCache[p.id];
                    const secteurs = d ? getAttrValues(d, ({ name, id }) => name === 'Secteur' || id === '211') : [];
                    const mobiliers = d ? getAttrValues(d, ({ name, id }) => name === 'Mobilier' || id === '210') : [];
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onSelectPoint(p)}
                        className="text-left rounded-lg border border-gray-200 p-3 hover:border-black transition-colors bg-white"
                      >
                        <div className="text-sm font-medium text-gray-900 truncate">{p.label}</div>
                        <div className="mt-1 text-xs text-gray-600">
                          {p.sap_code ? `Code SAP: ${p.sap_code}` : '—'}
                        </div>
                        <div className="text-xs text-gray-600">
                          Secteur: {secteurs.length ? secteurs.join(', ') : (detailLoadingKeys[p.id] ? 'Chargement…' : '—')}
                        </div>
                        <div className="text-xs text-gray-600">
                          Mobiliers: {mobiliers.length ? mobiliers.length : (detailLoadingKeys[p.id] ? '…' : '0')}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {detailLoading && (
                <div className="flex justify-center py-6">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
                </div>
              )}

              {detail && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-black">
                        {(
                          valuesFor(detail, ATTR_CONFIG.title[0])[0] ||
                          valuesFor(detail, ATTR_CONFIG.title[1])[0]
                        ) || selected?.label}
                      </h2>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setSelected(null); setDetail(null); }}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {ATTR_CONFIG.compact.map((field) => {
                      const vals = valuesFor(detail, field).join(', ');
                      return (
                        <span key={field.key} className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-800">
                          <span className="mr-1 text-gray-500">{field.label}:</span>
                          {vals || '—'}
                        </span>
                      );
                    })}
                  </div>

                  {/* Details - now displayed before Mobiliers, compact style and not collapsible */}
                  <InterventionPointsDetail detail={detail} orderList={ATTR_CONFIG.details} />

                  <div className="rounded border border-gray-200">
                    <div className="border-b px-3 py-2 text-sm font-medium text-gray-700">{ATTR_CONFIG.sections.mobiliers.label}</div>
                    <div className="p-3">
                      {valuesFor(detail, ATTR_CONFIG.sections.mobiliers).length === 0 ? (
                        <div className="text-sm text-gray-500">Aucun mobilier trouvé</div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {valuesFor(detail, ATTR_CONFIG.sections.mobiliers).map((m) => (
                            <span
                              key={m}
                              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs text-gray-800"
                            >
                              {m}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>



                  {/* Issues history for the selected intervention point */}
                  {(() => {
                    const keyFromAttr = getAttrValues(detail, ({ name, id }) => name === 'Key' || id === '189')[0];
                    const objectKey = keyFromAttr || selected?.key;
                    return (
                      <InterventionPointIssues objectKey={objectKey} />
                    );
                  })()}

                  {/* Deployments history - placeholder */}
                  <InterventionPointDeployments />

                  {/* Placeholder for tickets history (requires backend endpoint by intervention point) */}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


